import { useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  MapPin,
  Compass,
  Calendar,
  Camera,
  Upload,
  X,
  AlertCircle,
  Image,
  CloudSun,
  Leaf,
  Pencil,
  Trash2,
} from 'lucide-react'
import {
  getMonitoringPoint,
  uploadMonitoringPhoto,
  updateMonitoringPoint,
  deleteMonitoringPoint,
} from '../../services/api'
import { format, parseISO } from 'date-fns'
import clsx from 'clsx'
import { useAuth } from '../../contexts/AuthContext'

export default function MonitoringPointDetail({ ranchId }) {
  const { pointId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { userId } = useAuth()
  const fileInputRef = useRef(null)

  const [showUpload, setShowUpload] = useState(false)
  const [previewPhoto, setPreviewPhoto] = useState(null)
  const [uploadForm, setUploadForm] = useState({
    photo_date: new Date().toISOString().split('T')[0],
    weather_notes: '',
    vegetation_notes: '',
  })
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadError, setUploadError] = useState('')
  const [lightboxPhoto, setLightboxPhoto] = useState(null)
  const [showEdit, setShowEdit] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const { data: point, isLoading, error } = useQuery({
    queryKey: ['monitoringPoint', pointId],
    queryFn: () => getMonitoringPoint(pointId),
  })

  const uploadMutation = useMutation({
    mutationFn: (formData) => uploadMonitoringPhoto(pointId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitoringPoint', pointId] })
      queryClient.invalidateQueries({ queryKey: ['monitoringPoints'] })
      setShowUpload(false)
      setSelectedFile(null)
      setPreviewPhoto(null)
      setUploadForm({
        photo_date: new Date().toISOString().split('T')[0],
        weather_notes: '',
        vegetation_notes: '',
      })
    },
    onError: (err) => setUploadError(err.response?.data?.detail || 'Failed to upload photo'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteMonitoringPoint(pointId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitoringPoints'] })
      navigate('/land')
    },
  })

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setSelectedFile(file)
    setUploadError('')

    const reader = new FileReader()
    reader.onload = (ev) => setPreviewPhoto(ev.target.result)
    reader.readAsDataURL(file)
    setShowUpload(true)
  }

  const handleUploadSubmit = (e) => {
    e.preventDefault()
    if (!selectedFile) {
      setUploadError('Select a photo first')
      return
    }

    const formData = new FormData()
    formData.append('file', selectedFile)
    formData.append('photo_date', uploadForm.photo_date)
    if (uploadForm.weather_notes) formData.append('weather_notes', uploadForm.weather_notes)
    if (uploadForm.vegetation_notes) formData.append('vegetation_notes', uploadForm.vegetation_notes)
    formData.append('taken_by', userId)

    uploadMutation.mutate(formData)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error || !point) {
    return (
      <div className="card p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">Failed to load monitoring point</h3>
        <p className="text-gray-500 mt-1">{error?.message || 'Not found'}</p>
        <button
          onClick={() => navigate('/land')}
          className="btn bg-primary-600 text-white hover:bg-primary-700 mt-4"
        >
          Back to Monitoring Points
        </button>
      </div>
    )
  }

  const photos = point.photos || []

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/land')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{point.name}</h1>
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
              {point.paddock_code}
            </span>
            <span className={clsx(
              'px-2 py-0.5 rounded-full text-xs font-medium',
              point.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            )}>
              {point.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          {point.description && (
            <p className="text-gray-500 mt-1">{point.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowEdit(true)}
            className="btn bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 flex items-center gap-2 text-sm"
          >
            <Pencil className="h-4 w-4" />
            Edit
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="btn bg-white text-red-600 border border-red-200 hover:bg-red-50 flex items-center gap-2 text-sm"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Info Card */}
      <div className="card p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-blue-50">
              <MapPin className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">GPS</div>
              <div className="text-sm font-medium text-gray-900 mt-0.5">
                {point.latitude.toFixed(6)}, {point.longitude.toFixed(6)}
              </div>
            </div>
          </div>
          {point.compass_bearing != null && (
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-amber-50">
                <Compass className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider">Bearing</div>
                <div className="text-sm font-medium text-gray-900 mt-0.5">{point.compass_bearing}°</div>
              </div>
            </div>
          )}
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-purple-50">
              <Camera className="h-4 w-4 text-purple-600" />
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">Photos</div>
              <div className="text-sm font-medium text-gray-900 mt-0.5">{photos.length}</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-green-50">
              <Calendar className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">Created</div>
              <div className="text-sm font-medium text-gray-900 mt-0.5">
                {format(parseISO(point.created_at), 'dd MMM yyyy')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Add Photo</h2>
          {showUpload && (
            <button
              onClick={() => {
                setShowUpload(false)
                setSelectedFile(null)
                setPreviewPhoto(null)
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          )}
        </div>

        {!showUpload ? (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-green-400 hover:bg-green-50 transition-colors cursor-pointer"
          >
            <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 font-medium">Click to upload a photo</p>
            <p className="text-xs text-gray-400 mt-1">JPG, PNG or WebP — max 20MB</p>
          </button>
        ) : (
          <form onSubmit={handleUploadSubmit} className="space-y-4">
            {uploadError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {uploadError}
              </div>
            )}

            {/* Preview */}
            {previewPhoto && (
              <div className="relative rounded-lg overflow-hidden bg-gray-100 max-h-64 flex items-center justify-center">
                <img src={previewPhoto} alt="Preview" className="max-h-64 object-contain" />
                <button
                  type="button"
                  onClick={() => {
                    setSelectedFile(null)
                    setPreviewPhoto(null)
                    fileInputRef.current.value = ''
                    fileInputRef.current?.click()
                  }}
                  className="absolute top-2 right-2 p-1 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">Photo Date *</label>
                <input
                  type="date"
                  value={uploadForm.photo_date}
                  onChange={e => setUploadForm(prev => ({ ...prev, photo_date: e.target.value }))}
                  className="input w-full"
                  required
                />
              </div>
              <div>
                <label className="label">Weather Notes</label>
                <input
                  type="text"
                  value={uploadForm.weather_notes}
                  onChange={e => setUploadForm(prev => ({ ...prev, weather_notes: e.target.value }))}
                  placeholder="e.g. Overcast, light rain"
                  className="input w-full"
                />
              </div>
              <div>
                <label className="label">Vegetation Notes</label>
                <input
                  type="text"
                  value={uploadForm.vegetation_notes}
                  onChange={e => setUploadForm(prev => ({ ...prev, vegetation_notes: e.target.value }))}
                  placeholder="e.g. Tall grass, some regrowth"
                  className="input w-full"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowUpload(false)
                  setSelectedFile(null)
                  setPreviewPhoto(null)
                }}
                className="btn bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploadMutation.isPending || !selectedFile}
                className="btn bg-green-600 text-white hover:bg-green-700 flex items-center gap-2"
              >
                {uploadMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Camera className="h-4 w-4" />
                    Upload Photo
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Photo Timeline */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Photo Timeline
          {photos.length > 0 && (
            <span className="text-sm font-normal text-gray-500 ml-2">({photos.length} photos)</span>
          )}
        </h2>

        {photos.length === 0 ? (
          <div className="card p-12 text-center">
            <Image className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No photos yet</h3>
            <p className="text-gray-500 mt-1">Upload your first photo to start tracking vegetation changes</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {photos.map((photo) => (
              <div key={photo.id} className="card overflow-hidden group">
                <div
                  className="aspect-[4/3] bg-gray-100 relative cursor-pointer"
                  onClick={() => setLightboxPhoto(photo)}
                >
                  <img
                    src={photo.photo_url}
                    alt={`${point.name} — ${photo.photo_date}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none'
                      e.target.parentElement.classList.add('flex', 'items-center', 'justify-center')
                      const placeholder = document.createElement('div')
                      placeholder.className = 'text-center'
                      placeholder.innerHTML = '<svg class="h-8 w-8 text-gray-300 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>'
                      e.target.parentElement.appendChild(placeholder)
                    }}
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all" />
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                      <Calendar className="h-3.5 w-3.5 text-gray-400" />
                      {format(parseISO(photo.photo_date), 'dd MMM yyyy')}
                    </div>
                  </div>
                  {photo.weather_notes && (
                    <div className="flex items-start gap-1.5 text-xs text-gray-500 mb-1">
                      <CloudSun className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      {photo.weather_notes}
                    </div>
                  )}
                  {photo.vegetation_notes && (
                    <div className="flex items-start gap-1.5 text-xs text-gray-500">
                      <Leaf className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      {photo.vegetation_notes}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setLightboxPhoto(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg"
            onClick={() => setLightboxPhoto(null)}
          >
            <X className="h-6 w-6" />
          </button>
          <div className="max-w-4xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <img
              src={lightboxPhoto.photo_url}
              alt={`${point.name} — ${lightboxPhoto.photo_date}`}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="text-center mt-3 text-white">
              <div className="text-sm font-medium">
                {format(parseISO(lightboxPhoto.photo_date), 'dd MMMM yyyy')}
              </div>
              {lightboxPhoto.weather_notes && (
                <div className="text-xs text-gray-300 mt-1">{lightboxPhoto.weather_notes}</div>
              )}
              {lightboxPhoto.vegetation_notes && (
                <div className="text-xs text-gray-300 mt-0.5">{lightboxPhoto.vegetation_notes}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEdit && (
        <EditPointModal
          point={point}
          onClose={() => setShowEdit(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['monitoringPoint', pointId] })
            queryClient.invalidateQueries({ queryKey: ['monitoringPoints'] })
            setShowEdit(false)
          }}
        />
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 rounded-lg bg-red-50">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete monitoring point?</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    "{point.name}" will be hidden from the app. Its {photos.length} photo{photos.length === 1 ? '' : 's'} will remain on disk but become unreachable from the UI.
                  </p>
                </div>
              </div>
              {deleteMutation.error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm mb-3">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {deleteMutation.error?.response?.data?.detail || 'Failed to delete point'}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleteMutation.isPending}
                  className="btn bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                  className="btn bg-red-600 text-white hover:bg-red-700 flex items-center gap-2"
                >
                  {deleteMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function EditPointModal({ point, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: point.name || '',
    description: point.description || '',
    compass_bearing: point.compass_bearing != null ? String(point.compass_bearing) : '',
    is_active: point.is_active !== false,
  })
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: (data) => updateMonitoringPoint(point.id, data),
    onSuccess,
    onError: (err) => setError(err.response?.data?.detail || 'Failed to update point'),
  })

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }

    const bearing = formData.compass_bearing === ''
      ? null
      : parseInt(formData.compass_bearing, 10)
    if (bearing != null && (Number.isNaN(bearing) || bearing < 0 || bearing > 359)) {
      setError('Bearing must be 0-359')
      return
    }

    mutation.mutate({
      name: formData.name.trim(),
      description: formData.description || null,
      compass_bearing: bearing,
      is_active: formData.is_active,
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Edit Monitoring Point</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="label">Point Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => handleChange('name', e.target.value)}
              className="input w-full"
              required
            />
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              value={formData.description}
              onChange={e => handleChange('description', e.target.value)}
              placeholder="Landmark description to help relocate the spot..."
              className="input w-full"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Compass Bearing</label>
              <input
                type="number"
                value={formData.compass_bearing}
                onChange={e => handleChange('compass_bearing', e.target.value)}
                placeholder="0-359"
                className="input w-full"
                min="0"
                max="359"
              />
            </div>
            <div>
              <label className="label">Status</label>
              <label className="flex items-center gap-2 mt-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={e => handleChange('is_active', e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">
                  {formData.is_active ? 'Active' : 'Inactive'}
                </span>
              </label>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500">
            GPS coordinates are locked so repeat photos stay anchored to the same spot. Delete and re-create the point if the location is wrong.
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="btn bg-green-600 text-white hover:bg-green-700 flex items-center gap-2"
            >
              {mutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Pencil className="h-4 w-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
